import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Pencil, Trash2, Check, X, Zap,
  ChevronDown, Clock, MessageSquare, RefreshCw, Sparkles,
} from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { fmtRelative } from '../lib/helpers';
import { useToast } from '../components/layout/Toast';
import { Glass } from '../components/glass/Glass';
import { Btn } from '../components/primitives/Btn';
import { StatusDot } from '../components/primitives/StatusDot';
import { Md } from '../components/primitives/Md';
import { Collapsible } from '../components/cards/Collapsible';
import { isLocalAvailable, apiFetch } from '../lib/api';

// ─── Instruct Dialog ─────────────────────────────────────────────────────────
const InstructDialog = ({ agent, onClose, onUpdate }) => {
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [error, setError] = useState(null);
  const { addToast } = useToast();

  const run = async () => {
    if (!prompt.trim()) return;
    setError(null);
    const taskPrompt = prompt.trim();
    try {
      // Fire-and-forget: close dialog immediately so user can keep working
      addToast({ message: `${agent.name} is working on your request...`, type: 'info', duration: 6000 });
      apiFetch(`/agents/${agent.slug}/instruct`, {
        method: 'POST',
        body: JSON.stringify({ prompt: taskPrompt, context: context.trim() || null }),
      }).then(async (res) => {
        if (res.ok) {
          addToast({ message: `${agent.name} finished — check your inbox.`, type: 'success', duration: 8000 });
        } else {
          const data = await res.json().catch(() => ({}));
          addToast({ message: `${agent.name} failed: ${data.detail || 'unknown error'}`, type: 'error', duration: 8000 });
        }
        onUpdate();
      }).catch(() => {
        addToast({ message: `${agent.name} request failed — server may be down.`, type: 'error', duration: 8000 });
      });
      onUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
    border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
    fontFamily: 'inherit', color: T.text, outline: 'none', resize: 'vertical',
    lineHeight: 1.5,
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxHeight: '80vh', overflowY: 'auto',
          background: 'rgba(250,250,248,0.97)', backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.9)', borderRadius: 16, boxShadow: T.shadowLg,
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <StatusDot status={agent.status} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Instruct {agent.name}</div>
            {agent.expertise && <div style={{ fontSize: 11, color: T.textMuted }}>{agent.expertise}</div>}
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} color={T.textMuted} />
          </button>
        </div>

        <div style={{ padding: '18px 22px 22px' }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>What do you need?</div>
            <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4} placeholder="e.g. Draft a follow-up email to Rashid Khan..." style={inputStyle} autoFocus />
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Additional context <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></div>
            <textarea value={context} onChange={e => setContext(e.target.value)} rows={2} placeholder="Paste notes, reference info, constraints..." style={inputStyle} />
          </div>
          {error && <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn small variant="ghost" onClick={onClose}>Cancel</Btn>
            <button onClick={run} disabled={!prompt.trim()} style={{
              padding: '8px 20px', borderRadius: 10, border: 'none',
              background: T.amber, color: '#fff', cursor: !prompt.trim() ? 'default' : 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: !prompt.trim() ? 0.5 : 1,
            }}>
              <Zap size={12} /> Run
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Add Agent Dialog ────────────────────────────────────────────────────────
const AddAgentDialog = ({ onClose, onUpdate }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [expertise, setExpertise] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
    border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
    fontFamily: 'inherit', color: T.text, outline: 'none', resize: 'vertical', lineHeight: 1.5,
  };

  const generatePrompt = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/agents/create', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), description: description.trim(), expertise: expertise.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error);
      setSystemPrompt(data.suggested_prompt);
      setStep(2);
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const savePrompt = async () => {
    setLoading(true);
    try {
      const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      await apiFetch(`/agents/${slug}/instruct`, { method: 'POST', body: JSON.stringify({ prompt: 'Acknowledge. Reply with just "Ready."', context: null }) });
      onUpdate();
      onClose();
    } catch {
      onClose();
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxHeight: '80vh', overflowY: 'auto',
          background: 'rgba(250,250,248,0.97)', backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.9)', borderRadius: 16, boxShadow: T.shadowLg,
        }}
      >
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Create New Agent</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>Step {step} of 2</div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} color={T.textMuted} />
          </button>
        </div>

        <div style={{ padding: '18px 22px 22px' }}>
          {step === 1 ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Agent Name</div>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Comms Agent" style={{ ...inputStyle, resize: 'none' }} autoFocus />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Expertise</div>
                <input value={expertise} onChange={e => setExpertise(e.target.value)} placeholder="e.g. External comms, email drafting, social" style={{ ...inputStyle, resize: 'none' }} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>What should this agent do?</div>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Describe the agent's responsibilities..." style={inputStyle} />
              </div>
              {error && <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn small variant="ghost" onClick={onClose}>Cancel</Btn>
                <button onClick={generatePrompt} disabled={loading || !name.trim()} style={{
                  padding: '8px 20px', borderRadius: 10, border: 'none',
                  background: loading ? T.textLight : T.amber, color: '#fff', cursor: loading ? 'default' : 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {loading ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</> : <><Sparkles size={12} /> Generate with AI</>}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.green, letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Sparkles size={12} /> AI-GENERATED SYSTEM PROMPT
              </div>
              <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)} rows={12} style={{
                ...inputStyle, fontFamily: "'SF Mono', Menlo, Monaco, monospace", fontSize: 12, marginBottom: 18, lineHeight: 1.6,
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Btn small variant="ghost" onClick={() => setStep(1)}>Back</Btn>
                <button onClick={savePrompt} style={{
                  padding: '8px 20px', borderRadius: 10, border: 'none',
                  background: T.amber, color: '#fff', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                }}>Done</button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Add Routine Dialog ──────────────────────────────────────────────────────
const AddRoutineDialog = ({ agent, onClose, onUpdate }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleType, setScheduleType] = useState('on-demand');
  const [scheduleTime, setScheduleTime] = useState('07:00');
  const [scriptContent, setScriptContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scheduleOptions = [
    { id: 'on-demand', label: 'On demand', cron: null, human: 'On demand' },
    { id: 'daily', label: 'Daily', cron: null, human: null },
    { id: 'weekdays', label: 'Weekdays', cron: null, human: null },
    { id: 'weekly-mon', label: 'Weekly (Mon)', cron: null, human: null },
  ];

  const buildCron = () => {
    const [h, m] = scheduleTime.split(':').map(Number);
    if (scheduleType === 'daily') return { cron: `${m} ${h} * * *`, human: `Daily at ${scheduleTime}` };
    if (scheduleType === 'weekdays') return { cron: `${m} ${h} * * 1-5`, human: `Weekdays at ${scheduleTime}` };
    if (scheduleType === 'weekly-mon') return { cron: `${m} ${h} * * 1`, human: `Mondays at ${scheduleTime}` };
    return { cron: null, human: 'On demand' };
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
    border: '1px solid rgba(0,0,0,0.10)', background: 'rgba(255,255,255,0.8)',
    fontFamily: 'inherit', color: T.text, outline: 'none', resize: 'vertical', lineHeight: 1.5,
  };

  const generate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    const { cron, human } = buildCron();
    try {
      const res = await apiFetch('/routines/create', {
        method: 'POST',
        body: JSON.stringify({ agent_id: agent.id, name: name.trim(), description: description.trim(), schedule_cron: cron, schedule_human: human }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error);
      setScriptContent(data.script_content);
      setStep(2);
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={springs.gentle}
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxHeight: '80vh', overflowY: 'auto',
          background: 'rgba(250,250,248,0.97)', backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          border: '1px solid rgba(255,255,255,0.9)', borderRadius: 16, boxShadow: T.shadowLg,
        }}
      >
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>New Routine for {agent.name}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>Step {step} of 2</div>
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: 'none', background: 'rgba(0,0,0,0.04)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} color={T.textMuted} />
          </button>
        </div>

        <div style={{ padding: '18px 22px 22px' }}>
          {step === 1 ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Routine Name</div>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Weekly Investor Update" style={{ ...inputStyle, resize: 'none' }} autoFocus />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>What should it do?</div>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="Describe what this routine produces..." style={inputStyle} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Schedule</div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  {scheduleOptions.map(o => (
                    <button key={o.id} onClick={() => setScheduleType(o.id)} style={{
                      flex: 1, padding: '6px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                      background: scheduleType === o.id ? T.amberLight : 'rgba(0,0,0,0.02)',
                      color: scheduleType === o.id ? T.amber : T.textMuted,
                      border: `1.5px solid ${scheduleType === o.id ? T.amberBorder : 'rgba(0,0,0,0.06)'}`,
                    }}>{o.label}</button>
                  ))}
                </div>
                {scheduleType !== 'on-demand' && (
                  <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} style={{ ...inputStyle, width: 120, resize: 'none' }} />
                )}
              </div>
              {error && <div style={{ fontSize: 12, color: T.red, marginBottom: 12 }}>{error}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Btn small variant="ghost" onClick={onClose}>Cancel</Btn>
                <button onClick={generate} disabled={loading || !name.trim()} style={{
                  padding: '8px 20px', borderRadius: 10, border: 'none',
                  background: loading ? T.textLight : T.amber, color: '#fff', cursor: loading ? 'default' : 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {loading ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Drafting...</> : <><Sparkles size={12} /> Draft with AI</>}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.green, letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Sparkles size={12} /> AI-GENERATED SCRIPT
              </div>
              <textarea value={scriptContent} onChange={e => setScriptContent(e.target.value)} rows={16} style={{
                ...inputStyle, fontFamily: "'SF Mono', Menlo, Monaco, monospace", fontSize: 11, marginBottom: 18, lineHeight: 1.6,
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Btn small variant="ghost" onClick={() => setStep(1)}>Back</Btn>
                <button onClick={() => { onUpdate(); onClose(); }} style={{
                  padding: '8px 20px', borderRadius: 10, border: 'none', background: T.amber, color: '#fff',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                }}>Done</button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── Agents View ─────────────────────────────────────────────────────────────
export const AgentsView = ({ agents, agentLog, routines, instructions, onUpdate }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [instructAgent, setInstructAgent] = useState(null);
  const [addAgentOpen, setAddAgentOpen] = useState(false);
  const [addRoutineAgent, setAddRoutineAgent] = useState(null);
  const [runningRoutine, setRunningRoutine] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const runRoutine = async (routine) => {
    setRunningRoutine(routine.id);
    try {
      await apiFetch(`/routines/${routine.id}/run`, { method: 'POST' });
      onUpdate();
    } catch {} finally {
      setRunningRoutine(null);
    }
  };

  const startEdit = (agent) => {
    setEditingAgent({
      id: agent.id,
      name: agent.name,
      expertise: agent.expertise || '',
      description: agent.description || '',
      system_prompt: agent.system_prompt || '',
    });
  };

  const saveEdit = async () => {
    if (!editingAgent) return;
    setSavingEdit(true);
    await supabase.from('agents').update({
      name: editingAgent.name,
      expertise: editingAgent.expertise || null,
      description: editingAgent.description || null,
      system_prompt: editingAgent.system_prompt || null,
    }).eq('id', editingAgent.id);
    setEditingAgent(null);
    setSavingEdit(false);
    onUpdate();
  };

  const deleteAgent = async (agentId) => {
    await supabase.from('agents').delete().eq('id', agentId);
    setConfirmDelete(null);
    setExpandedId(null);
    onUpdate();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Agents</h1>
          <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
            {agents.filter(a => a.status !== 'idle').length} active · {agents.length} total
          </p>
        </div>
        <Btn variant="primary" onClick={() => setAddAgentOpen(true)}><Plus size={14} /> New Agent</Btn>
      </div>

      {/* Agent list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {agents.map((agent, agentIdx) => {
          const expanded = expandedId === agent.id;
          const agentRoutines = routines.filter(r => r.agent_id === agent.id);
          const agentInstructions = instructions.filter(i => i.agent_id === agent.id).slice(0, 5);
          const agentLogs = agentLog.filter(l => l.agent_id === agent.id).slice(0, 5);

          return (
            <motion.div key={agent.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: agentIdx * 0.04, ...springs.gentle }}>
              <Glass style={{ padding: 0, overflow: 'hidden' }} hover={!expanded}>
                {/* Collapsed header */}
                <div onClick={() => setExpandedId(expanded ? null : agent.id)} style={{
                  padding: '18px 22px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <StatusDot status={agent.status} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{agent.name}</span>
                      {agent.status === 'working' && (
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', color: T.amber, textTransform: 'uppercase' }}>Working</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
                      {agent.expertise || agent.description || agent.last_action || 'No description'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {agentRoutines.length > 0 && (
                      <span style={{ fontSize: 10, color: T.textMuted, background: 'rgba(0,0,0,0.04)', padding: '2px 8px', borderRadius: 10 }}>
                        {agentRoutines.filter(r => r.schedule_cron).length} routines
                      </span>
                    )}
                    {agent.last_active && <span style={{ fontSize: 10, color: T.textLight }}>{fmtRelative(agent.last_active)}</span>}
                    <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ color: T.textLight }}>
                      <ChevronDown size={14} />
                    </motion.span>
                  </div>
                </div>

                {/* Expanded content */}
                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={springs.gentle}
                      style={{ overflow: 'hidden', borderTop: '1px solid rgba(0,0,0,0.05)' }}
                    >
                      {/* Routines */}
                      <div style={{ padding: '16px 22px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textMuted }}>Routines</span>
                          <button onClick={(e) => { e.stopPropagation(); setAddRoutineAgent(agent); }} style={{
                            fontSize: 11, fontWeight: 500, color: T.amber, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                            display: 'flex', alignItems: 'center', gap: 4,
                          }}><Plus size={11} /> Add</button>
                        </div>
                        {agentRoutines.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {agentRoutines.map(r => (
                              <div key={r.id} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                borderRadius: 8, background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.04)',
                              }}>
                                <Clock size={12} color={T.textMuted} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: T.text }}>{r.name}</div>
                                  <div style={{ fontSize: 10, color: T.textMuted }}>{r.schedule_human || 'On demand'}</div>
                                </div>
                                {r.last_run_at && (
                                  <span style={{
                                    fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                                    background: r.last_run_status === 'success' ? T.greenLight : T.redLight,
                                    color: r.last_run_status === 'success' ? T.green : T.red,
                                  }}>{r.last_run_status === 'success' ? 'OK' : 'ERR'}</span>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); runRoutine(r); }} disabled={runningRoutine === r.id} style={{
                                  padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)',
                                  background: runningRoutine === r.id ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.8)',
                                  cursor: runningRoutine === r.id ? 'default' : 'pointer', fontSize: 11, fontWeight: 500,
                                  color: T.textMid, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
                                }}>
                                  {runningRoutine === r.id ? <><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Running</> : 'Run'}
                                </button>
                                <button onClick={async (e) => { e.stopPropagation(); await supabase.from('routines').delete().eq('id', r.id); onUpdate(); }} style={{
                                  padding: '4px 6px', borderRadius: 6, border: 'none', background: 'none', cursor: 'pointer',
                                }}>
                                  <Trash2 size={11} color={T.textLight} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: T.textMuted, fontStyle: 'italic' }}>No routines yet.</div>
                        )}
                      </div>

                      {/* Recent instructions */}
                      {agentInstructions.length > 0 && (
                        <div style={{ padding: '0 22px 16px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 10 }}>Recent Instructions</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {agentInstructions.map(instr => (
                              <div key={instr.id} style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                  <Collapsible icon={MessageSquare} title={instr.prompt.slice(0, 60) + (instr.prompt.length > 60 ? '...' : '')} subtitle={`${instr.status} · ${fmtRelative(instr.created_at)}`}>
                                    {instr.output ? <Md style={{ fontSize: 12 }}>{instr.output}</Md> : <div style={{ fontSize: 12, color: T.textMuted }}>{instr.status === 'running' ? 'Working...' : instr.error || 'No output'}</div>}
                                  </Collapsible>
                                </div>
                                <button onClick={async () => { await supabase.from('agent_instructions').delete().eq('id', instr.id); onUpdate(); }} style={{
                                  padding: 4, border: 'none', background: 'none', cursor: 'pointer', marginTop: 14, flexShrink: 0,
                                }}>
                                  <Trash2 size={11} color={T.textLight} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Activity log */}
                      {agentLogs.length > 0 && (
                        <div style={{ padding: '0 22px 16px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 10 }}>Activity</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {agentLogs.map(log => (
                              <div key={log.id} style={{ display: 'flex', gap: 8, padding: '4px 0', alignItems: 'center' }}>
                                <span style={{ fontSize: 10, color: T.textLight, fontFamily: "'SF Mono', Menlo, Monaco, monospace", minWidth: 40 }}>{fmtRelative(log.created_at)}</span>
                                <span style={{ fontSize: 12, color: T.textMid }}>{log.action}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Edit form */}
                      {editingAgent?.id === agent.id && (
                        <div style={{ padding: '16px 22px', borderTop: '1px solid rgba(0,0,0,0.05)', background: 'rgba(0,0,0,0.015)' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 10 }}>Edit Agent</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 4 }}>Name</div>
                              <input value={editingAgent.name} onChange={e => setEditingAgent({ ...editingAgent, name: e.target.value })} style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, border: '1px solid rgba(0,0,0,0.10)',
                                background: 'rgba(255,255,255,0.8)', fontFamily: 'inherit', color: T.text, outline: 'none',
                              }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 4 }}>Expertise</div>
                              <input value={editingAgent.expertise} onChange={e => setEditingAgent({ ...editingAgent, expertise: e.target.value })} placeholder="e.g. External comms, email drafting" style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, border: '1px solid rgba(0,0,0,0.10)',
                                background: 'rgba(255,255,255,0.8)', fontFamily: 'inherit', color: T.text, outline: 'none',
                              }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 4 }}>Description</div>
                              <textarea value={editingAgent.description} onChange={e => setEditingAgent({ ...editingAgent, description: e.target.value })} rows={2} style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, border: '1px solid rgba(0,0,0,0.10)',
                                background: 'rgba(255,255,255,0.8)', fontFamily: 'inherit', color: T.text, outline: 'none', resize: 'vertical', lineHeight: 1.5,
                              }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 4 }}>System Prompt</div>
                              <textarea value={editingAgent.system_prompt} onChange={e => setEditingAgent({ ...editingAgent, system_prompt: e.target.value })} rows={6} style={{
                                width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 11, border: '1px solid rgba(0,0,0,0.10)',
                                background: 'rgba(255,255,255,0.8)', fontFamily: "'SF Mono', Menlo, Monaco, monospace", color: T.text, outline: 'none', resize: 'vertical', lineHeight: 1.6,
                              }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                              <Btn small variant="ghost" onClick={() => setEditingAgent(null)}>Cancel</Btn>
                              <button onClick={saveEdit} disabled={savingEdit} style={{
                                padding: '6px 16px', borderRadius: 8, border: 'none', background: T.amber, color: '#fff',
                                cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', opacity: savingEdit ? 0.6 : 1,
                              }}>{savingEdit ? 'Saving...' : 'Save'}</button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action bar */}
                      <div style={{
                        padding: '14px 22px', borderTop: '1px solid rgba(0,0,0,0.05)',
                        display: 'flex', gap: 8, alignItems: 'center',
                      }}>
                        <button onClick={(e) => { e.stopPropagation(); setInstructAgent(agent); }} style={{
                          padding: '8px 18px', borderRadius: 10, border: 'none',
                          background: T.amber, color: '#fff', cursor: 'pointer',
                          fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}><MessageSquare size={12} /> Instruct</button>
                        <Btn small variant="ghost" onClick={(e) => { e.stopPropagation(); setAddRoutineAgent(agent); }}>
                          <Plus size={12} /> Add Routine
                        </Btn>
                        <Btn small variant="ghost" onClick={(e) => { e.stopPropagation(); startEdit(agent); }}>
                          <Pencil size={12} /> Edit
                        </Btn>
                        <div style={{ flex: 1 }} />
                        {confirmDelete === agent.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 11, color: T.red, fontWeight: 500 }}>Delete this agent?</span>
                            <button onClick={(e) => { e.stopPropagation(); deleteAgent(agent.id); }} style={{
                              padding: '4px 12px', borderRadius: 6, border: 'none', background: T.red, color: '#fff',
                              cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                            }}>Yes</button>
                            <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }} style={{
                              padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.03)',
                              cursor: 'pointer', fontSize: 11, fontWeight: 500, color: T.textMid, fontFamily: 'inherit',
                            }}>No</button>
                          </div>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setConfirmDelete(agent.id); }} style={{
                            padding: '4px 8px', borderRadius: 6, border: 'none', background: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                          }}>
                            <Trash2 size={13} color={T.textLight} />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Glass>
            </motion.div>
          );
        })}
      </div>

      {/* Dialogs */}
      <AnimatePresence>
        {instructAgent && <InstructDialog agent={instructAgent} onClose={() => setInstructAgent(null)} onUpdate={onUpdate} />}
      </AnimatePresence>
      <AnimatePresence>
        {addAgentOpen && <AddAgentDialog onClose={() => setAddAgentOpen(false)} onUpdate={onUpdate} />}
      </AnimatePresence>
      <AnimatePresence>
        {addRoutineAgent && <AddRoutineDialog agent={addRoutineAgent} onClose={() => setAddRoutineAgent(null)} onUpdate={onUpdate} />}
      </AnimatePresence>
    </motion.div>
  );
};
